import React, { useEffect, useState } from "react"
import { Label, Input, Alert } from "reactstrap"
import { useParams } from "react-router-dom"
import CurrencyInput from "react-currency-input-field"
import { useRedemptionData } from "../../../../Context/redemption"
import { usePurchaseData } from "../../../../Context/purchase"
import { useUserData } from "../../../../Context/user"
import { srcBgCharityPreselector1 } from "../../../../images"
import { Button } from "../../../"
import Modal from "../../../Shared/Modal"
import LoginModal from "../../../Common/LoginModal"
import UserRedemptionPageHeader from "../../UserRedemptionPageHeader"
import AddToDonationCheckout from "../../../../Contents/ShoppingCart/AddToDonationCheckout"
import { AccountTypes, AddToDonationCart } from "../../../../Code/Data"
import { isTestRedeemCode } from "../../../../Code/Utilities"
import styles from "./styles.module.scss"

function Step1({ onContinue, codeAsEntered, charityId, setCodeAsEntered, userId }) {
  const [agreeTerms, setAgreeTerms] = useState(false)
  const [isTermsModalOpen, setTermsModalOpen] = useState(false)
  const [codeError, setCodeError] = useState("")
  const [isChecking, setIsChecking] = useState(false)
  // Add-to-donation parity with the regular /Redeem flow (RedeemCodes Step1).
  // Recipients on a custom redemption page should also be able to top-up
  // their gift card before proceeding to charity selection.
  const [isAddDonationOpen, setAddDonationOpen] = useState(false)
  const [isActionModalOpen, setActionModalOpen] = useState(false)
  const [shoppingCart, setShoppingCart] = useState(null)
  const [paymentSuccess, setPaymentSuccess] = useState(false)
  const [isLoginModalOpen, setLoginModalOpen] = useState(false)
  const [pendingPay, setPendingPay] = useState(false)
  const [isPaying, setIsPaying] = useState(false)
  const { UserId: UserIdFromParams } = useParams()
  const { redemptionData, setRedemptionData, getRedeemCodeInfo, redemptionPageData,
    redeemCodeInfo, redemptionPageInfo, getRedemptionPageInfo, } = useRedemptionData()
  const { addToDonationData, setAddToDonationData } = usePurchaseData()
  const { loggedInUser, refreshUser, setUser } = useUserData()
  const { amount } = addToDonationData
  const { WelcomeMessage, HasRedeemed, BrandEcard, LogoURL } = redeemCodeInfo || {}
  const termsAndConditionsText = redeemCodeInfo?.DonorUserAccountType === AccountTypes.ThirdParty
    ? "CharityChoice is a fundraising project of Special Kids Fund (501c3, EIN 58-2550249), as such your confirmation and donation is 100% charitable. For gift cards purchased through third-party partners or redeeming points from rewards programs, when allocating funds to the chosen charities, we deduct a 10% fundraising cost and an additional affiliate fee required by the third party. Please consult your tax advisor regarding deductions and your specific tax situation."
    : "CharityChoice Gift Cards are recipient-designated charity gifts and a fundraising project of Special Kids Fund (a 501(c)(3) organization, EIN 58-2550249). Only the original gift recipient is entitled to designate the charity, which he or she may do at any time. Cards never lose value and do not expire. We always retain a reserve to honor unredeemed cards. Purchases of gift cards are charitable donations that are 100% tax-deductible at the time of purchase and, as such, are non-refundable. For cards purchased on our website, we deduct fundraising expenses of 5% plus $0.50 per card (for cards over $2) from the amount allocated to the charities. Allocations are distributed quarterly in a single bulk check (usually within 4-6 weeks after the close of the quarter) for all designations made during that quarter."

  // Test codes (TEST... / CORP...) are not associated with a real giver, so the
  // custom redemption page's logo/caption/colors must not be applied to them
  // even if the URL we landed on belongs to a real giver.
  const isTestCode = isTestRedeemCode(codeAsEntered)
  const displayData = (!!redemptionPageInfo
    ? redemptionPageInfo
    : (!!redeemCodeInfo && !!redeemCodeInfo.UserRedemptionPageInfo
      ? redeemCodeInfo.UserRedemptionPageInfo
      : {}))
  const { Caption, HeaderImageURL, MainColor, AccentColor } = displayData;


  useEffect(() => {
    // Only mirror the response's code into the input when the response
    // actually contains one. Failed lookups (e.g. invalid code) return
    // `{ Succeeded: false, RedeemInfo: null, ErrorMessage }` — no
    // RedemptionCodeAsEntered — and this effect was clearing the user's
    // typed code by writing `undefined` back to it, which also wiped the
    // error message before it could render.
    if (
      redeemCodeInfo?.RedemptionCodeAsEntered &&
      redeemCodeInfo.RedemptionCodeAsEntered !== codeAsEntered
    ) {
      setCodeAsEntered(redeemCodeInfo.RedemptionCodeAsEntered)
    }
  }, [redeemCodeInfo])

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  // Debounce the code lookup so it only fires after the user pauses typing,
  // and clean the timer up on unmount/re-keystroke. Combined with the
  // request-seq guard in RedemptionDataProvider, this prevents the
  // "valid code typed but Continue stays disabled" race.
  useEffect(() => {
    if (!codeAsEntered) {
      setCodeError("")
      setIsChecking(false)
      return
    }
    const upper = codeAsEntered.toUpperCase()
    if (redeemCodeInfo && upper === (redeemCodeInfo.RedemptionCodeAsEntered || "").toUpperCase()) {
      // Already have the response for this exact code — no need to re-check.
      setIsChecking(false)
      return
    }
    // User is typing a new code. Clear any stale error from a previous code
    // and show the "checking" indicator until the debounced lookup resolves.
    setCodeError("")
    setIsChecking(true)
    const timer = setTimeout(async () => {
      const result = await getRedeemCodeInfo(upper, (parseInt(UserIdFromParams) || userId))
      setIsChecking(false)
      if (!result) return // Stale response — a newer lookup is already in flight.
      if (result.HasRedeemed) {
        setCodeError("This code has already been redeemed.")
      } else if (!result.Succeeded) {
        setCodeError(result.ErrorMessage || "We couldn't validate that code. Please double-check and try again.")
      } else {
        setCodeError("")
      }
    }, 1000)
    return () => clearTimeout(timer)
  }, [codeAsEntered, userId])

  const handleChangeCode = async () => {
    const upper = (codeAsEntered || "").toUpperCase()
    if (!upper) return
    if (
      !redeemCodeInfo?.RedemptionCodeAsEntered ||
      upper !== redeemCodeInfo.RedemptionCodeAsEntered.toUpperCase()
    ) {
      await getRedeemCodeInfo(upper, (parseInt(UserIdFromParams) || userId));     
    }
  }

  const handleChangeData = e => {
    setRedemptionData({
      ...redemptionData,
      [e.target.name]: e.target.value,
    })
  }

  const dataValidation = () => {
    if (!agreeTerms || HasRedeemed || !codeAsEntered || !redeemCodeInfo) return false
    if (isChecking) return false
    // Only enable Continue when the *current* code's lookup has resolved and
    // succeeded. Without this check, leftover redeemCodeInfo from a previous
    // code (or an in-flight lookup) could let the user click Continue with
    // stale validation data.
    if (!redeemCodeInfo.Succeeded) return false
    const enteredUpper = codeAsEntered.toUpperCase()
    const respondedUpper = (redeemCodeInfo.RedemptionCodeAsEntered || "").toUpperCase()
    return enteredUpper === respondedUpper
  }

  const handleContinue = async () => {
    // If the user has typed an add-to-donation amount but hasn't completed the
    // payment yet, confirm what they want to do before advancing.
    if (amount > 0 && !paymentSuccess) {
      if (!isActionModalOpen) {
        setActionModalOpen(true)
        return
      } else {
        setActionModalOpen(false)
        setAddToDonationData({ ...addToDonationData, amount: 0 })
      }
    }
    setRedemptionData({
      ...redemptionData,
      codeAsEntered,
    })
    onContinue()
  }

  const handleChangeAmount = event => {
    setAddToDonationData({
      ...addToDonationData,
      amount: event === undefined ? 0 : event,
    })
  }

  const handlePaymentSuccess = () => {
    setPaymentSuccess(true)
  }

  const onPay = async () => {
    // Re-entry guard. Without this, a fast double-click on "Add $X Now"
    // fires AddToDonationCart twice (and the backend cheerfully adds two
    // donation items to the cart — visible in the server response showing
    // multiple duplicate $X "Added Donation" rows).
    if (isPaying) return
    setActionModalOpen(false)
    // PayPal SDK buttons are gated by `loggedInUser` inside
    // AddToDonationCheckout, so opening it for a logged-out user shows an
    // empty checkout area. Prompt for login first.
    if (!loggedInUser) {
      setPendingPay(true)
      setLoginModalOpen(true)
      return
    }
    setIsPaying(true)
    try {
      const result = await AddToDonationCart(amount)
      if (result?.Succeeded) {
        // The cart can contain prior add-to-donation items (Items[0] in the
        // response is often a leftover from earlier). The just-added item is
        // always at the end of the list — pick that one.
        const items = result.ShoppingCart?.Items || []
        setShoppingCart(items[items.length - 1] || null)
        setAddDonationOpen(true)
      }
    } finally {
      setIsPaying(false)
    }
  }

  const handleLoginSuccess = user => {
    setLoginModalOpen(false)
    if (user && setUser) setUser(user)
    else if (refreshUser) refreshUser()
    // The useEffect below picks up the now-truthy loggedInUser and resumes
    // the top-up flow. We can't call onPay() directly here because its
    // closure was captured when loggedInUser was still false, so the
    // !loggedInUser branch would loop us back into the login modal.
  }

  // Resume the pending Add-to-donation flow once login has completed and
  // loggedInUser has actually propagated through context.
  useEffect(() => {
    if (loggedInUser && pendingPay) {
      setPendingPay(false)
      if (isPaying) return
      setIsPaying(true)
        ; (async () => {
          try {
            const result = await AddToDonationCart(amount)
            if (result?.Succeeded) {
              const items = result.ShoppingCart?.Items || []
              setShoppingCart(items[items.length - 1] || null)
              setAddDonationOpen(true)
            }
          } finally {
            setIsPaying(false)
          }
        })()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loggedInUser, pendingPay])

  const logoImage =
    BrandEcard && LogoURL ? (
      <div className={styles.title}>
        <img className={styles.imgLogo} src={LogoURL} alt="Corporate Logo" />
      </div>
    ) : null

  return (<>
    <UserRedemptionPageHeader headerImageURL={HeaderImageURL} welcomeMessage={WelcomeMessage} caption={Caption} mainColor={MainColor} />
    <div className={styles.container} style={{ backgroundColor: MainColor }}>
      <img
        className={styles.bgBackground}
        src={srcBgCharityPreselector1}
        alt=""
      />
      <div className={styles.form}>
        {logoImage}
        <div className={styles.title} style={{ color: MainColor }}
          dangerouslySetInnerHTML={{ __html: WelcomeMessage || "Let's redeem your Charity Gift!" }}>
        </div>
        <div className={styles.redemptionDetail}>
          <div className={styles.inutWrapper}>
            <input
              className={styles.inputCode}
              style={{ color: MainColor, borderColor: MainColor }}
              name="code"
              value={codeAsEntered}
              onChange={e => setCodeAsEntered(e.target.value.toUpperCase())}
            />
            <div
              className={styles.linkChangeCode}
              style={{ color: AccentColor }}
              onClick={handleChangeCode}>
              {!!codeAsEntered ? "Change Code" : "Enter Code"}
            </div>
          </div>
          <div className={styles.inputWrapper}>
            <input
              className={styles.inputEmail}
              style={{ color: MainColor, borderColor: MainColor }}
              name="email"
              value={redemptionData?.email || ""}
              placeholder="Email Address"
              onChange={handleChangeData}
            />
          </div>
          <div className={styles.lblEmail} style={{ color: MainColor }}>
            Optional: To receive a confirmation
          </div>
        </div>
        {/* Live status feedback for the redeem code: checking / invalid / accepted */}
        <div className={styles.codeStatus} role="status" aria-live="polite">
          {isChecking && (
            <span className={styles.codeChecking}>Checking your code...</span>
          )}
          {!isChecking && dataValidation() && (
            <span className={styles.codeOk} style={{ color: AccentColor }}>
              <i className="fa-solid fa-circle-check" /> Code accepted
            </span>
          )}
        </div>
        <Label check className={styles.termsWrapper}>
          <Input
            type="checkbox"
            onChange={() => setAgreeTerms(!agreeTerms)}
          />{" "}
          <span className={styles.lblTerms}>
            I affirm that I am the gift recipient or that I received this code
            in a rewards program by me redeeming my points. View our{" "}
            <button
              type="button"
              className={styles.termsLinkButton}
              onClick={event => {
                event.preventDefault()
                event.stopPropagation()
                setTermsModalOpen(true)
              }}>
              Terms and Conditions
            </button>.
          </span>
        </Label>
        {(codeError || HasRedeemed) && (
          <Alert color="danger">
            {codeError || "This code has already been redeemed."}
          </Alert>
        )}
        {/* Add-to-donation top-up — only visible once the code is valid and
            the gift card isn't already locked to a specific charity. */}
        {dataValidation() && !redeemCodeInfo?.HasCharityId && !paymentSuccess && (
          <div className={styles.addDonationSection}>
            {amount > 0 && isTestCode && (
              <div className={styles.addToDonationTestMsg}>
                To add, please use a non-test redemption code
              </div>
            )}
            <div className={styles.divAddToDonation}>
              <div>To add to this donation, enter an amount</div>
              <div title="When you designate your charity gift, you may add your own funds to increase the benefit for your favorite charities. The total sum of your charity gift and whatever you have added will be divided equally amongst the chosen charities. You will receive an emailed tax receipt for the additional funds.">
                <CurrencyInput
                  className={styles.amountInput}
                  onValueChange={handleChangeAmount}
                  allowNegativeValue={false}
                  prefix={"$"}
                  placeholder="$0"
                  value={amount ? amount.toString() : ""}
                />
              </div>
              {amount > 0 && !isTestCode && (
                <button
                  type="button"
                  className={styles.addToDonationBtn}
                  onClick={() => onPay()}
                  disabled={isPaying}
                >
                  {isPaying ? "Adding..." : `Add $${amount} Now`}
                </button>
              )}
            </div>
          </div>
        )}
        {paymentSuccess && (
          <div className={styles.donationAddedMsg}>
            You have added ${amount || redeemCodeInfo?.AddedToDonationAmount} to this donation.
          </div>
        )}
        <div className={styles.btnContinue}>
          <Button
            style={{ backgroundColor: AccentColor, borderColor: AccentColor }}
            disabled={!dataValidation()}
            onClick={handleContinue}>
            Continue
          </Button>
        </div>
      </div>
    </div>    <Modal
      isOpen={isTermsModalOpen}
      onClose={() => setTermsModalOpen(false)}
      contentLabel="Terms and Conditions"
    >
      <h3 className="mb-3">Terms and Conditions</h3>
      <p>{termsAndConditionsText}</p>
    </Modal>
    <Modal
      isOpen={isActionModalOpen}
      onClose={() => setActionModalOpen(false)}
      contentLabel="Confirm donation addition"
    >
      <div className={styles.actionModal}>
        <div className={styles.actionModalTitle}>
          You entered <span>${amount}</span> to top up your donation
        </div>
        <div className={styles.actionModalDesc}>What would you like to do?</div>
        <div className={styles.actionModalBtns}>
          <button
            className={styles.btnAddNow}
            onClick={() => onPay()}
            disabled={isPaying}
          >
            {isPaying ? "Adding..." : `Add $${amount} Now`}
          </button>
          <button
            className={styles.btnContinueNoAdd}
            onClick={() => handleContinue()}
            disabled={isPaying}
          >
            Continue Without Adding
          </button>
        </div>
      </div>
    </Modal>
    <LoginModal
      isOpen={isLoginModalOpen}
      caption="Log in to top up your donation"
      onClose={() => { setLoginModalOpen(false); setPendingPay(false); }}
      onSuccess={handleLoginSuccess}
    />
    <Modal
      isOpen={isAddDonationOpen}
      onClose={() => { setAddDonationOpen(false); setShoppingCart(null) }}
      contentLabel="Add to Donation Checkout"
    >
      <AddToDonationCheckout
        item={shoppingCart}
        amount={amount}
        onPaymentSuccess={handlePaymentSuccess}
        code={codeAsEntered}
      />
    </Modal>
  </>
  )
}

export default Step1
